<template>
    <div class="container tercero-page">
        <div class="param-title-wrap">
            <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" />
            </b-button>
            <h2 class="param-title">{{ labels.title }}</h2>
        </div>
        
        <div class="tercero-table-block mt-4" v-if="listZscore?.length > 0">
            <CorporateDataTable
                :items="listZscore"
                :fields="allFields"
                :filter-defs="tableFilterDefs"
                :labels="tableLabels"
                :default-per-page="10"
                :page-options="[10, 15, 20, 50, 100]"
                :narrow="false"
                :table-min-height="500"
                :table-max-height="500"
                @row-clicked="(item) => editZscore(item.id, item.vigencia)"
            >
                <template #toolbar-actions>
                    <b-button size="sm" class="table-toolbar-add-btn" @click="createZscore()">
                        {{ labels.addZscore }}
                    </b-button>
                </template>
                
                <!-- Columnas con formato numérico -->
                <template #cell(activo_corriente)="data">
                    <div class="text-right">{{ formatNumber(data.item?.activo_corriente) }}</div> 
                </template>
                <template #cell(activo_no_corriente)="data">
                    <div class="text-right">{{ formatNumber(data.item?.activo_no_corriente) }}</div> 
                </template>
                <template #cell(total_activo)="data">
                    <div class="text-right">{{ formatNumber(data.item?.total_activo) }}</div> 
                </template>
                <template #cell(pasivo_corriente)="data">
                    <div class="text-right">{{ formatNumber(data.item?.pasivo_corriente) }}</div> 
                </template>
                <template #cell(pasivo_no_corriente)="data">
                    <div class="text-right">{{ formatNumber(data.item?.pasivo_no_corriente) }}</div> 
                </template>
                <template #cell(total_pasivo)="data">
                    <div class="text-right">{{ formatNumber(data.item?.total_pasivo) }}</div> 
                </template>
                <template #cell(utilidad_acumulada)="data">
                    <div class="text-right">{{ formatNumber(data.item?.utilidad_acumulada) }}</div> 
                </template>
                <template #cell(total_patrimonio)="data">
                    <div class="text-right">{{ formatNumber(data.item?.total_patrimonio) }}</div> 
                </template>
            </CorporateDataTable>
        </div>
        
        <div class="row col-md-12 justify-content-center mt-3" v-else>
            <h5>{{ labels.noRow }}</h5> 
        </div>
        
        <Crear :modalCrear="showModCrear" @creado="loadData" @cierraModal="closeModal"></Crear>
        <Detalle :modalEditar="showModEditar" :infoFinanciera="dataFinanciera" :infoConstante="infoConstante" @cierraModal="closeModal"></Detalle>
    </div>
</template>

<script>
import Crear from './Crear.vue'; 
import Detalle from './Detalle.vue'; 
import InformacionFinanciera from '../../../services/InformacionFinanciera'; 
import Constante from '../../../services/Constante'; 
import CorporateDataTable from '../../utils/CorporateDataTable.vue';

export default {
    name: 'InformacionFinanciera',
    components: { Crear, Detalle, CorporateDataTable },
    
    data: () => ({
        listZscore: [],
        showModCrear: false,
        showModEditar: false,
        dataFinanciera: [],
        dataConstante: [],
        infoConstante: []
    }),
    
    computed: {
        // Labels optimizados con operador ternario
        labels() {
            const isSpanish = this.$store.state._language === "es-ES";
            return {
                title: isSpanish ? 'Información Financiera' : 'Financial Information',
                noRow: isSpanish ? "No hay registros" : "There are no records",
                addZscore: isSpanish ? 'Añadir Zscore' : 'Add Zscore',
                vigencia: isSpanish ? 'Vigencia' : 'Validity',
                activoCorriente: isSpanish ? 'Activo corriente' : 'Current assets',
                activoNoCorriente: isSpanish ? 'Activo no corriente' : 'Non-current assets',
                totalActivo: isSpanish ? 'Total activo' : 'Total assets',
                pasivoCorriente: isSpanish ? 'Pasivo corriente' : 'Current liabilities',
                pasivoNoCorriente: isSpanish ? 'Pasivo no corriente' : 'Non-current liabilities',
                totalPasivo: isSpanish ? 'Total pasivo' : 'Total liabilities',
                utilidadAcumulada: isSpanish ? 'Utilidad acumulada' : 'Accumulated profit',
                totalPatrimonio: isSpanish ? 'Total patrimonio' : 'Total equity',
                // Filtros
                filterVigencia: isSpanish ? 'Buscar por vigencia' : 'Search by validity',
                filterActivoCorriente: isSpanish ? 'Buscar activo corriente' : 'Search current assets',
                filterActivoNoCorriente: isSpanish ? 'Buscar activo no corriente' : 'Search non-current assets',
                filterTotalActivo: isSpanish ? 'Buscar total activo' : 'Search total assets',
                filterPasivoCorriente: isSpanish ? 'Buscar pasivo corriente' : 'Search current liabilities',
                filterPasivoNoCorriente: isSpanish ? 'Buscar pasivo no corriente' : 'Search non-current liabilities',
                filterTotalPasivo: isSpanish ? 'Buscar total pasivo' : 'Search total liabilities',
                filterUtilidadAcumulada: isSpanish ? 'Buscar utilidad acumulada' : 'Search accumulated profit',
                filterTotalPatrimonio: isSpanish ? 'Buscar total patrimonio' : 'Search total equity'
            };
        },
        
        // Etiquetas para la tabla
        tableLabels() {
            return {
                searchPlaceholder: this.labels.filterVigencia || 'Buscar...',
                perPage: this.$store.state._language === "es-ES" ? 'Registros por página' : 'Records per page',
                noRecords: this.labels.noRow
            };
        },
        
        // Configuración de campos de la tabla
        allFields() {
            return [
                { 
                    key: 'vigencia', 
                    label: this.labels.vigencia, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-center', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '100px' } 
                },
                { 
                    key: 'activo_corriente', 
                    label: this.labels.activoCorriente, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '120px' } 
                },
                { 
                    key: 'activo_no_corriente', 
                    label: this.labels.activoNoCorriente, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '120px' } 
                },
                { 
                    key: 'total_activo', 
                    label: this.labels.totalActivo, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '140px' } 
                },
                { 
                    key: 'pasivo_corriente', 
                    label: this.labels.pasivoCorriente, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '140px' } 
                },
                { 
                    key: 'pasivo_no_corriente', 
                    label: this.labels.pasivoNoCorriente, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '140px' } 
                },
                { 
                    key: 'total_pasivo', 
                    label: this.labels.totalPasivo, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '140px' } 
                },
                { 
                    key: 'utilidad_acumulada', 
                    label: this.labels.utilidadAcumulada, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '140px' } 
                },
                { 
                    key: 'total_patrimonio', 
                    label: this.labels.totalPatrimonio, 
                    sortable: true, 
                    thClass: 'text-center', 
                    tdClass: 'text-end', 
                    thStyle: { whiteSpace: 'nowrap', minWidth: '140px' } 
                }
            ];
        },
        
        // Configuración de filtros de la tabla
        tableFilterDefs() {
            return [
                { key: 'vigencia', type: 'text', placeholder: this.labels.filterVigencia },
                { key: 'activo_corriente', type: 'text', placeholder: this.labels.filterActivoCorriente },
                { key: 'activo_no_corriente', type: 'text', placeholder: this.labels.filterActivoNoCorriente },
                { key: 'total_activo', type: 'text', placeholder: this.labels.filterTotalActivo },
                { key: 'pasivo_corriente', type: 'text', placeholder: this.labels.filterPasivoCorriente },
                { key: 'pasivo_no_corriente', type: 'text', placeholder: this.labels.filterPasivoNoCorriente },
                { key: 'total_pasivo', type: 'text', placeholder: this.labels.filterTotalPasivo },
                { key: 'utilidad_acumulada', type: 'text', placeholder: this.labels.filterUtilidadAcumulada },
                { key: 'total_patrimonio', type: 'text', placeholder: this.labels.filterTotalPatrimonio },
            ];
        }
    },
    
    methods: {
        goBack() {
            this.$router.back();
        },
        
        createZscore() {
            this.showModCrear = true;
        },
        
        editZscore(id, vigencia) {
            InformacionFinanciera.getInfoFinancieraById(id)
                .then((res) => {
                    this.dataFinanciera = res.data.infoFinancieraById[0];
                    this.showModEditar = true;
                })
                .catch((err) => console.log(err));

            Constante.getConstanteByVigencia(vigencia)
                .then((res) => {
                    this.dataConstante = res.data.constanteByVigencia;
                    const pos = this.dataConstante.length - 1;
                    this.infoConstante = this.dataConstante[pos];
                })
                .catch((err) => console.log(err));
        },
        
        closeModal(val) {
            this.showModCrear = val;
            this.showModEditar = val;
            // Recargar datos al cerrar el modal
            if (!val) {
                this.loadData();
            }
        },
        
        formatNumber(value) {
            return new Intl.NumberFormat('es-CO', { 
                minimumFractionDigits: 0, 
                maximumFractionDigits: 3 
            }).format(value);
        },
        
        loadData() {
            InformacionFinanciera.getAllInformacionFinanciera()
            .then((res) => {
                this.listZscore = res.data.infoFinanciera;
            })
            .catch((err) => console.log(err));
        }
    },
    
    mounted() {
        this.loadData();
    }
};
</script>

<style scoped>
/* Estilos específicos del componente */
.tercero-page {
    padding: 20px;
}

.param-title-wrap {
    display: flex;
    align-items: center;
}

.param-title {
    margin: 0;
    font-size: 1.5rem;
}

.page-back-btn {
    font-size: 1.5rem;
    padding: 0;
}

.text-end {
    text-align: right !important;
}

.text-center {
    text-align: center !important;
}

.table-toolbar-add-btn {
    background-color: #007bff;
    color: white;
    border: none;
    padding: 0.375rem 0.75rem;
}

.table-toolbar-add-btn:hover {
    background-color: #0069d9;
}
</style>